import type {Location, NavigateOptions as RouterNavigateOptions, RouterProviderProps, To} from '@github-ui/react-router'
import {startSoftNav} from '@github-ui/soft-nav/state'
import {createPath, parsePath, type Path} from 'history'

type Router = RouterProviderProps['router']

/**
 * Staff magic params that should be preserved across navigations.
 * These are used for feature flags and tracing in development/staff environments.
 */
const STAFF_MAGIC_PARAMS = ['_features', '_tracing'] as const

/**
 * Wraps the router's navigate function to add support for Turbo and Soft Navigation.
 *
 * When this is called, immediately `startSoftNav` if necessary
 * Apply a default `skipTurbo` state override to all router navigations
 * Preserve staff magic params (`_features`, `_tracing`) from the current URL
 *
 * This ensures that we mark react soft navigations correctly as well as ensure turbo doesn't
 * incorrectly handle react navigations.
 *
 * @param router The router to wrap.
 * @returns A new router with the wrapped navigate function.
 */
export function wrapRouterNavigateForTurboAndSoftNavs(router: Router): Router {
  return new Proxy(router, {
    get(target, prop, receiver) {
      if (prop === 'navigate') {
        return function (to: To | null | number, opts?: RouterNavigateOptions): Promise<void> {
          if (typeof to === 'number') {
            return target.navigate(to)
          }

          const currentLocation = target.state.location
          const nextLocation = typeof to === 'string' ? parsePath(to) : to

          // Preserve staff magic params from current URL
          const toWithMagicParams = preserveStaffMagicParams(nextLocation)

          if (toWithMagicParams && shouldHardNavigate(target, currentLocation, nextLocation)) {
            const destinationUrl =
              typeof toWithMagicParams === 'string' ? toWithMagicParams : createPath(toWithMagicParams)
            // For code view, if navigating to a different repo, do a full page reload to avoid stale data issues
            navigation.hardNavigate(destinationUrl)
            return Promise.resolve()
          }

          const hashNav = isHashNav(currentLocation, nextLocation)
          if (!hashNav) {
            startSoftNav('react')
          }

          const isPushAction = !opts?.replace
          const skipTurboState = opts?.state?.skipTurbo

          return target.navigate(toWithMagicParams, {
            ...opts,
            // Prevent scroll reset on hash-only navigations to avoid jumping to top
            // before virtualized content can scroll to the target line
            preventScrollReset: hashNav ? true : opts?.preventScrollReset,
            state: {
              ...opts?.state,
              skipTurbo: isPushAction ? (skipTurboState ?? true) : skipTurboState,
            },
          })
        }
      }

      // Pass through all other props
      return Reflect.get(target, prop, receiver)
    },
  })
}

// exposed for testing and easier mocking
export const navigation = {
  hardNavigate(url: string) {
    window.location.href = url
  },
}

function shouldHardNavigate(target: Router, currentLocation: Location, nextLocation: Partial<Path> | null): boolean {
  try {
    const isCodeView = !!target.state?.loaderData?.codeViewLayoutRoute
    return Boolean(
      isCodeView && nextLocation?.pathname && !isSameRepoPath(currentLocation.pathname, nextLocation.pathname),
    )
  } catch {
    return true
  }
}

function isSameRepoPath(url1: string, url2: string): boolean {
  const path1 = url1.split('/', 3).join('/')
  const path2 = url2.split('/', 3).join('/')
  return path1 === path2
}

/**
 * Preserves staff magic params (`_features`, `_tracing`) from the current browser URL
 * to the destination URL if they aren't already present in the destination.
 */
function preserveStaffMagicParams(to: Partial<Path> | null): To | null {
  if (!to) return null

  const currentSearchParams = new URLSearchParams(window.location.search)
  const destinationSearchParams = new URLSearchParams(to.search || '')

  let modified = false
  for (const param of STAFF_MAGIC_PARAMS) {
    const currentValue = currentSearchParams.get(param)
    if (currentValue && !destinationSearchParams.has(param)) {
      destinationSearchParams.set(param, currentValue)
      modified = true
    }
  }

  if (!modified) {
    return to
  }

  const newSearch = destinationSearchParams.toString()
  return createPath({
    ...to,
    search: newSearch ? `?${newSearch}` : '',
  })
}

function isHashNav(current: Partial<Path>, next: Partial<Path> | null): boolean {
  if (!next) return false

  if (next.pathname !== undefined && next.pathname !== current.pathname) return false
  if (next.search !== undefined && next.search !== current.search) return false

  return Boolean(next.hash !== undefined && next.hash !== current.hash)
}
